import { Component, Output, EventEmitter, Input, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplicationDetailsService } from '../../loan-applications/loan-application-details/loan-application-details.service';
import { ApplicationDetailsDTO } from '../../loan-applications/loan-application-details/loan-application-details.interface';
import { LoanApplicationService } from '../../loan-applications/loan-applications.service';
import { LoanApplicationItem, LoanApplicationUpdateDTO } from '../../loan-applications/loan-applications.interface';
import { UserLoginService } from 'src/app/login/userlogin.service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { LosStateService } from '../los-state.service';

interface CheckItem {
  label: string;
  parameterName: string;
  passed: boolean;
}

@Component({
  selector: 'app-disbursal-ready',
  templateUrl: './disbursal-ready.component.html',
  styleUrls: ['./disbursal-ready.component.css']
})
export class DisbursalReadyComponent implements OnInit {
  @Input() applicationId: number | null = null;
  @Output() next = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();

  private applicationDetailsService = inject(ApplicationDetailsService);
  private loanApplicationService = inject(LoanApplicationService);
  private userLoginService = inject(UserLoginService);
  private losState = inject(LosStateService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  applicationData: LoanApplicationItem | null = null;
  isLoading = false;
  isMarking = false;

  checklist: CheckItem[] = [
    { label: 'User consent recorded', parameterName: 'User_Consent', passed: false },
    { label: 'Aadhaar verified', parameterName: 'Aadhaar_Verified', passed: false },
    { label: 'PAN verified', parameterName: 'PAN_Verified', passed: false },
    { label: 'Financial details captured', parameterName: 'Monthly_Income', passed: false },
    { label: 'Occupation type captured', parameterName: 'Occupation', passed: false }
  ];

  get allChecksPassed(): boolean {
    const statusOk = this.applicationData?.applicationStatus === 'Approved' ||
                     this.applicationData?.applicationStatus === 'DisbursalReady';
    return statusOk && this.checklist.every(c => c.passed);
  }

  get isAlreadyReady(): boolean {
    return this.applicationData?.applicationStatus === 'DisbursalReady';
  }

  ngOnInit(): void {
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => this.applicationData = app);

    this.fetchChecklist();
  }

  fetchChecklist(): void {
    if (!this.applicationId) return;
    this.isLoading = true;

    this.applicationDetailsService.ApplicationDetails_ReadByApplicationId({
      applicationId: this.applicationId
    }).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res?.items?.length) {
          this.evaluateChecklist(res.items);
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Failed to fetch checklist:', err);
      }
    });
  }

  private evaluateChecklist(items: ApplicationDetailsDTO[]): void {
    for (const check of this.checklist) {
      const found = items.find(i => i.parameterName === check.parameterName);
      check.passed = !!(found?.value && found.value.trim().length > 0 && found.value !== '0');
    }
  }

  async markDisbursalReady(): Promise<void> {
    if (!this.applicationData || !this.applicationId) return;
    if (this.isMarking) return;
    this.isMarking = true;

    try {
      const payload: LoanApplicationUpdateDTO = {
        applicationId: this.applicationData.applicationId,
        userId: this.applicationData.userId,
        productId: this.applicationData.productId,
        applicationStatus: 'DisbursalReady',
        requestedAmount: this.applicationData.requestedAmount,
        approvedAmount: this.applicationData.approvedAmount,
        tenureMonths: this.applicationData.tenureMonths,
        applicationDate: this.applicationData.applicationDate,
        approvalDate: this.applicationData.approvalDate,
        approvedBy: this.applicationData.approvedBy || '',
        rejectionReason: this.applicationData.rejectionReason || '',
        rejectedBy: this.applicationData.rejectedBy || '',
        isDisbursed: this.applicationData.isDisbursed,
        disbursedOn: this.applicationData.disbursedOn,
        disbursedAmount: this.applicationData.disbursedAmount,
        subUserId: this.applicationData.subUserId,
        businessId: this.applicationData.businessId,
        productCode: this.applicationData.productCode || '',
        prrn: this.applicationData.prrn,
        lmsApplicationName: this.applicationData.lmsApplicationName,
        otherDetail1: this.applicationData.otherDetail1,
        otherDetail2: this.applicationData.otherDetail2,
        otherDetail3: this.applicationData.otherDetail3,
        otherDetail4: this.applicationData.otherDetail4,
        otherDetail5: this.applicationData.otherDetail5,
        otherDetail6: this.applicationData.otherDetail6,
        isActive: 1,
        actionUser: this.userLoginService.User()?.userId?.toString() || '0'
      };

      const updated = await firstValueFrom(this.loanApplicationService.LoanApplication_Update(payload));
      this.losState.patchApplication(updated);
      this.toastr.success('Application marked as Disbursal Ready');
      this.isMarking = false;
      this.next.emit();
    } catch (err) {
      this.toastr.error('Failed to update status');
      console.error(err);
      this.isMarking = false;
    }
  }
}
